"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Star,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  ChevronRight,
  Crop,
  X,
  Tag,
  Check,
  Loader2,
  Building2,
  IdCard
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import useTemplateBase, {
  mergePropertyListsWithCurrent,
  resolvePropertyListName,
} from "./TemplateBase";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import openHouseBadge from "../../assets/star_red.png";

interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
}

interface Property {
  id: number;
  propertyListType?: {
    id: number;
    list_name: string;
    custom_template_data?: Record<string, any>;
  };
  title: string;
  listing_id: string;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  property_type?: string | null;
  area?: number | null;
  status?: string | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  original_image?: string | null;
  photos?: string[] | null;
  custom_template_data?: Record<string, any> | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template2Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updated: Partial<any>) => void;
  onPropertyListChange?: (s: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string; // ✅ ADD THIS PROP - same as template1
  readOnly?: boolean; // ✅ ADD THIS PROP - same as template1
}

const boxHeroRes = "420px X 650px";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same helper in Template1.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

const Template2: React.FC<Template2Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  onPropertyListCreated,
  selectedList: viewSelectedList, // ✅ ADD THIS - same as template1
  readOnly = false, // ✅ ADD THIS - same as template1
}) => {
  const { toast } = useToast();

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Mirrors the same state in Template1.tsx.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  // Custom list creation state
  const [isCustomList, setIsCustomList] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] =
    useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<
    string | null
  >(null);
  const RESOLUTION_HINT =
    "For best results, upload an image matching the box resolution.";
  // ✅ USE TEMPLATE BASE HOOK - SAME AS TEMPLATE1
  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    selectedPropertyList: externalSelectedList,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  // Sync local property lists when prop changes
  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  // Custom template data handling for template2
  const existingCustomData2 =
    property?.propertyListType?.custom_template_data?.template2 || [];
  const [customFields2, setCustomFields2] = useState<
    { key: string; value: string }[]
  >(Array.isArray(existingCustomData2) ? existingCustomData2 : []);
  const [editingCustom2, setEditingCustom2] = useState(false);

  const addCustomField2 = () => {
    if (readOnly) return;
    setCustomFields2((p) => [...p, { key: "", value: "" }]);
  };

  const updateCustomField2 = (i: number, field: "key" | "value", v: string) => {
    if (readOnly) return;
    setCustomFields2((p) => {
      const n = [...p];
      n[i] = { ...n[i], [field]: v };
      return n;
    });
  };

  const removeCustomField2 = (i: number) => {
    if (readOnly) return;
    setCustomFields2((p) => p.filter((_, idx) => idx !== i));
  };

  const saveCustomForTemplate2 = async () => {
    if (readOnly) return;
    try {
      const token = localStorage.getItem("access_token");
      if (property?.propertyListType?.id) {
        await axiosInstance.patch(
          `${endpoints.propertylisttype}${property.propertyListType.id}/`,
          {
            custom_template_data: {
              ...(property.propertyListType.custom_template_data || {}),
              template2: customFields2,
            },
          },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
      }
      setEditingCustom2(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed saving custom fields",
        variant: "destructive",
      });
    }
  };

  // ✅ FIX: Determine currently selected list - SAME LOGIC AS TEMPLATE1
  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ?? // ✅ ADD THIS - was missing!
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Just Listed";

  // Provide defaults if empty
  const allLists = mergePropertyListsWithCurrent(
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Just Listed" },
        { id: 2, list_name: "Priced to Sell" },
        { id: 3, list_name: "Open House" },
        { id: 4, list_name: "Seller Motivated" },
      ],
    property?.propertyListType
  );

  const listSelectValue =
    resolvePropertyListName(
      selectedList,
      property?.propertyListType?.list_name
    ) || "Just Listed";

  // === SAVE CUSTOM LIST ===
  const saveCustomList = async () => {
    if (readOnly) return;

    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected. Please select a property first.",
        variant: "destructive",
      });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");

      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);

      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });

      setIsCustomList(false);
      setCustomListName("");

      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and saved to database`,
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);

      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (readOnly) return;

    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  // Enhanced file handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const result = reader.result as string;
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();

          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;

          tmp.src = result;
        });

        // Show resolution toast
        setTimeout(() => {
          toast({
            title: "Resolution Suggestion",
            description: `Uploaded image is ${imgEl.naturalWidth}x${imgEl.naturalHeight}. Recommended: ${boxHeroRes}.`,
            duration: 4000,
          });
        }, 100);

      } catch (err) {
        console.error("Image resolution read failed:", err);
      }
      setFlyerData((prev) => ({ ...prev, heroImage: result }));
      // This freshly-picked photo becomes the new "original" for this session
      // immediately, so Edit Image always has an untouched source to re-crop
      // from, right from this point on. Mirrors Template1.tsx.
      setOriginalHeroImage(result);
      onDirtyChange?.(true);
      onChange?.({ heroImage: result });
      toast({
        title: "Image Uploaded",
        description: "Property image has been updated successfully",
      });
    };
    reader.readAsDataURL(file);

    // Persist this freshly-picked photo as the untouched "original" separately
    // from the displayed image, so Edit Image can always re-crop from the full
    // source later instead of re-cropping an already-cropped result. Immediate
    // and independent of the deferred "Save Changes" flow. Mirrors Template1.tsx.
    if (property?.id) {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("original_image", file);
      axiosInstance
        .patch(`${endpoints.properties}${property.id}/`, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        })
        .catch((err) => {
          console.error("Failed to save original image:", err);
        });
    }
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 260;
  const EDIT_CROP_H = 402; // matches the 420x650 hero box aspect ratio

  const openEditImage = async () => {
    if (readOnly || !flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleSecondaryImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();

          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;

          tmp.src = dataUrl;
        });

        // Show resolution toast
        setTimeout(() => {
          toast({
            title: "Resolution Suggestion",
            description: `Uploaded image is ${imgEl.naturalWidth}x${imgEl.naturalHeight}. Recommended: ${boxHeroRes}.`,
            duration: 4000,
          });
        }, 100);

      } catch (err) {
        console.error("Image resolution read failed:", err);
      }
      setFlyerData((prev) => ({
        ...prev,
        secondaryImage: dataUrl,
      }));

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        const uploadRes = await axiosInstance.post(
          endpoints.mls_uploads,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (uploadRes?.data?.url) {
          const imageUrl = uploadRes.data.url;
          setFlyerData((prev) => {
            const existing = Array.isArray(prev.photos)
              ? prev.photos.filter((p) => p && p !== imageUrl)
              : [];
            const nextPhotos = [imageUrl, ...existing]
              .filter(Boolean)
              .slice(0, 1);
            const next = {
              ...prev,
              secondaryImage: imageUrl,
              photos: nextPhotos,
            };
            onDirtyChange?.(true);
            onChange?.({ secondaryImage: imageUrl, photos: nextPhotos });
            return next;
          });

          toast({
            title: "Success",
            description: "Secondary photo uploaded and saved",
            duration: 1000,
          });
        }
      } catch (err) {
        console.error("Failed to upload secondary image:", err);
        toast({
          title: "Upload Error",
          description: "Failed to upload secondary photo to server",
          variant: "destructive",
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // === OPEN HOUSE LOGIC ===
  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (readOnly) return;
    if (!property?.id || selectedList !== "Open House") return;

    setIsOpenHouseConfirmed(confirmed);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });

      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed
          ? "Special styling enabled"
          : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save choice",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  return (
    <div
      className={`
      w-full
   ${readOnly ? "overflow-x-visible template2-preview-scroll-host" : "overflow-x-auto"}
      scrollbar-thin
      scrollbar-thumb-stone-400
      scrollbar-track-transparent
    `}
      style={{
        WebkitOverflowScrolling: "touch",
        transform: "rotateX(180deg)",
      }}
    >
      <div
        className={`${readOnly ? "bg-white" : "bg-stone-50"} shadow-none rounded-2xl overflow-hidden font-arial relative border ${readOnly ? "block w-full template2-preview-card" : "mx-auto inline-block align-top"
          }`}
        style={{
          ...(readOnly ? { width: "100%" } : {}),
          minWidth: "740px",
          transform: "rotateX(180deg)",
        }}
      >
        {/* Custom Fields Editor Modal */}
        {editingCustom2 && !readOnly && (
          <div className="absolute right-6 top-20 z-50 bg-white p-4 rounded-lg shadow-xl w-80 border border-stone-200">
            <div className="flex items-center justify-between mb-3">
              <strong className="text-stone-900">Custom Fields</strong>
              <div className="flex gap-2">
                <button
                  onClick={addCustomField2}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Add
                </button>
                <button
                  onClick={() => setEditingCustom2(false)}
                  className="text-sm text-stone-600 hover:text-stone-800"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {customFields2.map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2">
                  <input
                    value={f.key}
                    onChange={(e) =>
                      updateCustomField2(idx, "key", e.target.value)
                    }
                    placeholder="Key"
                    className="border px-2 py-1 flex-1 text-sm rounded"
                  />
                  <input
                    value={f.value}
                    onChange={(e) =>
                      updateCustomField2(idx, "value", e.target.value)
                    }
                    placeholder="Value"
                    className="border px-2 py-1 flex-1 text-sm rounded"
                  />
                  <button
                    onClick={() => removeCustomField2(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={saveCustomForTemplate2}
                className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Top Bar */}
        <div className="bg-gradient-to-r from-stone-900 to-stone-800 text-white px-6 md:px-8 py-4 md:py-5">
          <div className="flex flex-row justify-between items-center gap-4">
            {/* Property List Selector */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Tag className="h-5 w-5 text-stone-400 flex-shrink-0" />

              <div className="flex items-center gap-3 whitespace-nowrap">
                {/* LIST TAG */}
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
                  {/* ✅ ADD READONLY CHECK - same as template1 */}
                  {readOnly ? (
                    <span className="text-white font-semibold text-sm uppercase tracking-wide">
                      {listSelectValue || "Just Listed"}
                    </span>
                  ) : isCustomList ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={customListName}
                        onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                        placeholder="Custom tag (max 20 chars)"
                        maxLength={20}
                        className="bg-white/20 text-white placeholder-white/60 px-2 py-1 rounded text-sm w-28 md:w-32 outline-none border border-white/30 focus:border-white/60"
                        autoFocus
                        disabled={isSavingCustomList}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isSavingCustomList) {
                            e.preventDefault();
                            saveCustomList();
                          }
                          if (e.key === "Escape") {
                            cancelCustomList();
                          }
                        }}
                      />
                      <button
                        onClick={saveCustomList}
                        disabled={!customListName.trim() || isSavingCustomList}
                        className="p-1.5 bg-white/20 rounded hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Save custom tag"
                      >
                        {isSavingCustomList ? (
                          <Loader2 className="h-4 w-4 text-white animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 text-white" />
                        )}
                      </button>
                      <button
                        onClick={cancelCustomList}
                        disabled={isSavingCustomList}
                        className="p-1.5 bg-white/20 rounded hover:bg-white/30 disabled:opacity-50 transition-colors"
                        title="Cancel"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <select
                      value={listSelectValue}
                      onChange={(e) => handleListSelection(e.target.value)}
                      className="bg-transparent text-white px-2 py-1 rounded font-semibold text-sm border-0 outline-none cursor-pointer"
                    >
                      {allLists
                        .filter((l) => l.list_name !== "Open House")
                        .map((l) => (
                          <option
                            key={l.id}
                            value={l.list_name}
                            className="text-stone-900 bg-white"
                          >
                            {l.list_name}
                          </option>
                        ))}

                      <option
                        value="__custom__"
                        className="text-stone-900 bg-white font-medium"
                      >
                        + Add Custom Tag
                      </option>
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="flex-shrink-0 text-right">
              {readOnly ? (
                <p className="text-2xl md:text-3xl font-bold text-white text-right">
                  {flyerData.price
                    ? `$${Number(flyerData.price).toLocaleString()}`
                    : "$0"}
                </p>
              ) : (
                <input
                  type="text"
                  value={
                    flyerData.price
                      ? `$${Number(flyerData.price).toLocaleString()}`
                      : ""
                  }
                  onChange={(e) => {
                    const num = e.target.value.replace(/[^0-9]/g, "");
                    handleInputChange("price", num ? Number(num) : null);
                  }}
                  className="text-2xl md:text-3xl font-bold bg-transparent border-none outline-none text-white placeholder-white/40 text-right"
                  placeholder="$0"
                />
              )}
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className={`grid grid-cols-[420px_1fr] gap-0 min-w-[740px]${readOnly ? " w-full" : ""}`}>
          {/* Left Side – Hero Image + Description */}
          <div className="relative bg-white overflow-hidden">
            {/* Hero Image */}
            <div className="relative w-full md:w-[420px] h-[650px] md:min-w-[420px] bg-stone-200 group">


              { /* Resolution Hint - only when NO image and in edit mode */}
              {!readOnly && (
                <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-black/70 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    {boxHeroRes ? (
                      <div className="leading-snug">
                        <div className="font-semibold">Required image resolution: {boxHeroRes}</div>
                        <div className="mt-1 text-[11px]">Please upload exactly this resolution.</div>
                      </div>
                    ) : (
                      <div className="leading-snug">{RESOLUTION_HINT}</div>
                    )}
                  </div>
                </div>
              )}
              {isOpenHouseConfirmed && (
                <div className="absolute top-4 right-4 md:top-1 md:right-0 z-20">
                  <div className="relative w-24 md:w-32">
                    <img
                      src={openHouseBadge}
                      alt="Open House"
                      className="w-full drop-shadow-xl"
                    />
                    <span
                      className="absolute inset-0 flex items-center justify-center
                   text-white font-extrabold text-xs md:text-sm
                   tracking-wide text-center pointer-events-none"
                    >
                      OPEN
                      <br />
                      HOUSE
                    </span>
                  </div>
                </div>
              )}

              {flyerData.heroImage ? (
                <>
                  <img
                    src={flyerData.heroImage}
                    alt="Property"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* ✅ Only show hover overlay when NOT readOnly */}
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                      <Button
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Change Image
                      </Button>
                      <Button
                        size="sm"
                        onClick={openEditImage}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Crop className="h-4 w-4 mr-2" />
                        Edit Image
                      </Button>
                    </div>
                  )}
                </>
              ) : !readOnly ? (
                <label
                  htmlFor="hero-upload-t2"
                  className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer hover:bg-stone-100 transition-colors"
                >
                  <ImageIcon className="h-16 w-16 md:h-20 md:w-20 text-stone-400 mb-4" />
                  <p className="text-xl md:text-2xl font-bold text-stone-600">
                    Upload Property Photo
                  </p>
                  <p className="text-sm text-stone-500 mt-2">
                    Click to select image
                  </p>
                </label>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <ImageIcon className="h-16 w-16 md:h-20 md:w-20 text-stone-400 mb-4" />
                  <p className="text-xl md:text-2xl font-bold text-stone-600">
                    No Image
                  </p>
                </div>
              )}

              {/* ✅ Only render file input when NOT readOnly */}
              {!readOnly && (
                <input
                  id="hero-upload-t2"
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />
              )}

              {!readOnly && (
                <ImageCropModal
                  open={cropModal.open}
                  imageUrl={cropModal.imageUrl}
                  title="Adjust Property Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleEditImageConfirm}
                  onCancel={handleEditImageCancel}
                />
              )}
            </div>

            {/* Description */}
            <div className="p-6 md:p-8">
              <h3 className="font-semibold text-stone-900 mb-4 uppercase tracking-wider text-sm flex items-center">
                <Star className="h-4 w-4 mr-2 text-stone-600" />
                Property Description
              </h3>
              {readOnly ? (
                <p className="leading-relaxed text-stone-600 text-base whitespace-pre-wrap">
                  {flyerData.description || "No description provided."}
                </p>
              ) : (
                <textarea
                  value={flyerData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="w-full bg-transparent border-none outline-none resize-none leading-relaxed text-stone-600 text-base placeholder-stone-400 focus:text-stone-900"
                  rows={5}
                  placeholder="Enter a compelling property description that highlights the unique features and lifestyle this home offers..."
                />
              )}
            </div>
          </div>

          {/* Right Side – Details */}
          <div className="bg-white p-6 md:p-8 flex flex-col min-h-0 border-t md:border-t-0 md:border-l border-stone-200">
            {/* Headline */}
            <div className="mb-6">
              {readOnly ? (
                <p className="text-3xl md:text-4xl font-serif font-bold text-stone-900 leading-tight">
                  {flyerData.headline || "Stunning Property Awaits"}
                </p>
              ) : (
                <textarea
                  value={flyerData.headline}
                  onChange={(e) => handleInputChange("headline", e.target.value)}
                  className="text-3xl md:text-4xl font-serif font-bold text-stone-900 w-full bg-transparent border-none outline-none resize-none leading-tight placeholder-stone-400"
                  rows={2}
                  placeholder="Stunning Property Awaits"
                />
              )}
            </div>

            {/* Address */}
            <div className="flex items-start mb-6 pb-6 border-b border-stone-200">
              <MapPin className="mr-3 h-5 w-5 text-stone-400 mt-1 flex-shrink-0" />
              {readOnly ? (
                <p className="text-stone-600 text-base">
                  {flyerData.address || "No address provided"}
                </p>
              ) : (
                <input
                  type="text"
                  value={flyerData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-stone-600 placeholder-stone-400 text-base"
                  placeholder="123 Main Street, City, State 12345"
                />
              )}
            </div>

            {/* Stats */}
            <div className="flex justify-between mb-8 py-6 border-y border-stone-200">
              {[
                { icon: Bed, label: "Beds", field: "bedrooms" as const },
                { icon: Bath, label: "Baths", field: "bathrooms" as const },
                { icon: Square, label: "Sq Ft", field: "area" as const },
              ].map((item, i) => (
                <React.Fragment key={item.field}>
                  {i > 0 && <div className="w-px bg-stone-200" />}
                  <div className="text-center flex-1">
                    <item.icon className="h-6 w-6 md:h-7 md:w-7 text-stone-500 mx-auto mb-3" />
                    {readOnly ? (
                      <p className="text-2xl md:text-3xl font-bold text-stone-900">
                        {item.field === "area"
                          ? flyerData.area ?? flyerData.sqft ?? 0
                          : flyerData[item.field] ?? 0}
                      </p>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        value={item.field === "area"
                          ? flyerData.area ?? flyerData.sqft ?? ""
                          : flyerData[item.field] ?? ""}
                        onChange={(e) =>
                          handleInputChange(
                            item.field,
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        className="text-2xl md:text-3xl font-bold text-stone-900 w-full text-center bg-transparent border-none outline-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    )}
                    <p className="text-xs text-stone-500 uppercase tracking-wider mt-2 font-medium">
                      {item.label}
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Key Features */}
            <div className="flex-1">
              <h3 className="font-semibold text-stone-900 mb-5 uppercase tracking-wider text-sm flex items-center justify-between">
                <span className="flex items-center">
                  <Star className="mr-2 h-5 w-5 text-yellow-500" />
                  Key Features
                </span>
                {!readOnly && (
                  <span className="text-xs normal-case text-stone-500 font-normal">
                    {flyerData.features.length}/10
                  </span>
                )}
              </h3>
              <div className="space-y-3">
                {flyerData.features.map((f, i) => (
                  <div
                    key={f.id ?? `feature-${i}`}
                    className="group flex items-center gap-3 bg-stone-50 hover:bg-stone-100 rounded-lg px-4 py-4 transition-all duration-200 border border-stone-100"
                  >
                    {/* Icon */}
                    <ChevronRight className="h-4 w-4 text-stone-400 flex-shrink-0" />

                    {/* Feature Text */}
                    <div className="flex-1 min-w-0">
                      {readOnly ? (
                        <p className="text-stone-700 leading-6 break-words">
                          {f.feature_name || "Feature"}
                        </p>
                      ) : (
                        <textarea
                          value={f.feature_name || ""}
                          onChange={(e) => handleFeatureChange(i, e.target.value)}
                          onBlur={() => handleFeatureBlur(i)}
                          maxLength={30}
                          rows={1}
                          className="w-full bg-transparent resize-none border-none outline-none text-stone-700 placeholder-stone-400 leading-6 overflow-hidden"
                          placeholder="(max 30 chars)..."
                        />
                      )}
                    </div>

                    {/* Delete Button */}
                    {!readOnly && (
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(i);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(i);
                        }}
                        className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity text-xl font-bold flex-shrink-0"
                        aria-label="Delete feature"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                {!readOnly && flyerData.features.length < 10 && (
                  <Button
                    onClick={addFeature}
                    variant="ghost"
                    className="text-stone-600 hover:text-stone-900 font-medium text-sm w-full justify-start px-4 hover:bg-stone-100"
                  >
                    + Add Feature
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Agent Section */}
        {/* Footer - Agent Section */}
        <div className="bg-gradient-to-r from-stone-900 to-stone-800 text-white px-4 py-5">
          <div className="w-full flex flex-row items-center justify-between gap-5">

            {/* Agent Section */}
            <div className="flex items-center gap-3 flex-1 min-w-0">

              {/* Avatar - Agent */}
              <div className="w-[150px] h-[180px] self-stretch rounded-xl overflow-hidden shadow-lg flex items-center justify-center shrink-0">
                {agentData?.avatar ? (
                  <img
                    src={agentData.avatar}
                    alt="Agent"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-stone-500" />
                )}
              </div>

              {/* Agent Info + Company (grouped) */}
              <div className="flex flex-col min-w-0 flex-1 justify-center items-center gap-1 text-justify ml-9 pl-9">

                {/* Name */}
                <p className="text-lg font-semibold text-white truncate">
                  {flyerData.agentName || "Agent Name"}
                </p>

                {/* Phone */}
                <div className="inline-flex items-center gap-1.5 mx-auto">
                  <Phone className="h-4 w-4 text-stone-400 shrink-0" />
                  <span className="text-sm text-stone-300">{flyerData.agentPhone || "N/A"}</span>
                </div>

                {/* Email */}
                <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                  <Mail className="h-4 w-4 text-stone-400 shrink-0" />
                  <span className="text-sm text-stone-300 underline">
                    {/* {flyerData.agentEmail || "N/A"} */}
                     Click here to email Me
                    </span>
                   
                </div>

                {/* Company Name */}
                <div className="inline-flex items-center gap-1.5 mx-auto">
                  <Building2 className="w-4 h-4 text-stone-400 shrink-0" />
                  <span className="text-sm font-medium text-stone-200">{flyerData.company || "Company Name"}</span>
                </div>

                {/* License Number */}
                {agentData?.license_number && (
                  <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                    <IdCard className="h-4 w-4 text-stone-400 shrink-0" />
                    <span className="text-sm text-stone-300">
                      License #: {agentData.license_number}
                    </span>
                  </div>
                )}

              </div>

              {/* Company Section */}
              <div className="flex items-center gap-3 sm:pl-5 self-stretch">

                {/* Avatar - Company Logo */}
                <div className="w-[180px] h-[180px] rounded-xl overflow-hidden shadow-lg flex items-center justify-center shrink-0">
                  {agentData?.company_logo ? (
                    <img
                      src={agentData.company_logo}
                      alt="Company Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xs text-stone-400">Logo</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template2;
